import React from 'react';
import { motion } from 'framer-motion';
import { MoreVertical, Calendar, User, ExternalLink } from 'lucide-react';
import { TFileItem } from '@/data/types';
import FileDropdown from './FileDropdown';

interface FileGridCardProps {
  file: TFileItem;
  index: number;
  openDropdown: string | null;
  onDropdownToggle: (fileId: string) => void;
  getFileIcon: (file: TFileItem) => React.ReactNode;
  formatFileSize: (bytes: number | null | undefined) => string;
  formatDate: (dateString: string) => string;
  getFileCategory: (file: TFileItem) => string;
  canDownloadFile: (file: TFileItem) => boolean;
  onPreview: (file: TFileItem) => void;
  onDownload: (file: TFileItem) => void;
  onMove: (file: TFileItem) => void;
  onDelete: (file: TFileItem) => void;
  setAbacFile: (file: TFileItem | null) => void;
}

const FileGridCard: React.FC<FileGridCardProps> = React.memo(
  ({
    file,
    index,
    openDropdown,
    onDropdownToggle,
    getFileIcon,
    formatFileSize,
    formatDate,
    getFileCategory,
    canDownloadFile,
    onPreview,
    onDownload,
    onMove,
    onDelete,
    setAbacFile,
  }) => {
    const isExternalLink = file.external_url && !file.protected_url;

    return (
      <div
        draggable
        onDragStart={(e: React.DragEvent) => {
          e.dataTransfer.setData('text/plain', file.id);
          (e.currentTarget as HTMLElement).style.opacity = '0.5';
        }}
        onDragEnd={(e: React.DragEvent) => {
          (e.currentTarget as HTMLElement).style.opacity = '1';
        }}
        className="group animate-in fade-in-0 slide-in-from-bottom-3 relative cursor-move rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-gray-300 hover:shadow-md"
      >
        {/* External Link Indicator */}
        {isExternalLink && (
          <div className="absolute top-2 right-2 z-10">
            <div className="rounded-md bg-green-100 p-1">
              <ExternalLink className="h-3 w-3 text-green-600" />
            </div>
          </div>
        )}

        {/* File Icon */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-gray-50">{getFileIcon(file)}</div>
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDropdownToggle(file.id);
              }}
              className="rounded-lg p-1.5 opacity-50 transition-all duration-200 group-hover:opacity-100 hover:bg-gray-100"
              data-dropdown-trigger
            >
              <MoreVertical className="h-4 w-4 text-gray-400" />
            </motion.button>

            {openDropdown === file.id && (
              <FileDropdown
                file={file}
                onClose={() => onDropdownToggle('')}
                canDownloadFile={canDownloadFile}
                onPreview={onPreview}
                onDownload={onDownload}
                onMove={onMove}
                onDelete={onDelete}
                onAbacEdit={() => setAbacFile(file)}
              />
            )}
          </div>
        </div>

        {/* File Info */}
        <div className="mb-3">
          <h3 className="mb-1 truncate text-sm font-semibold text-gray-900" title={file.name}>
            {file.name}
          </h3>
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
            <span className="inline-flex rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
              {getFileCategory(file)}
            </span>
          </div>
        </div>

        {/* Description */}
        {file.description && (
          <div className="mb-3">
            <p className="line-clamp-1 text-xs text-gray-600" title={file.description}>
              {file.description}
            </p>
          </div>
        )}

        {/* File Meta */}
        <div className="flex items-center justify-between border-t border-gray-100 pt-3 text-xs text-gray-500">
          <span>{formatDate(file.created_at)}</span>
          {file.owner && <span title={`${file.owner.first_name} ${file.owner.last_name}`}>Owner</span>}
        </div>
      </div>
    );
  },
);

FileGridCard.displayName = 'FileGridCard';

export default FileGridCard;
